import { Star, Quote } from "lucide-react";
import { useInView } from "../hooks/useInView";

const reviews = [
  {
    name: "Alex Rivera",
    role: "Founder, NovaTech",
    image: "https://images.unsplash.com/photo-1769071166862-8cc3a6f2ac5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzdGFydHVwJTIwZm91bmRlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTA3OTAyNXww&ixlib=rb-4.1.0&q=85&w=200&h=200",
    text: "Relio found 3 warm intro paths to our lead investor that I didn't even know existed. Closed our Series A in 6 weeks instead of 6 months.",
    rating: 5,
  },
  {
    name: "Priya Sharma",
    role: "CEO, Meridian Health",
    image: "https://images.unsplash.com/photo-1758691737644-ef8be18256c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxzdGFydHVwJTIwZm91bmRlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTA3OTAyNXww&ixlib=rb-4.1.0&q=85&w=200&h=200",
    text: "The AI email drafts save me 2 hours every day. Claude understands the nuance of each relationship — it's like having a chief of staff for my inbox.",
    rating: 5,
  },
  {
    name: "Marcus Chen",
    role: "GP, Horizon Ventures",
    image: "https://images.unsplash.com/photo-1765438869321-d60141efd813?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxzdGFydHVwJTIwZm91bmRlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTA3OTAyNXww&ixlib=rb-4.1.0&q=85&w=200&h=200",
    text: "We track 500+ founder relationships. Relio's warmth decay alerts have saved at least a dozen deals from going cold. Indispensable tool.",
    rating: 5,
  },
];

export default function AboutReviews() {
  const [aboutRef, aboutInView] = useInView();
  const [reviewsRef, reviewsInView] = useInView();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="pl-8 md:pl-16 pr-4 md:pr-8 py-16 md:py-24"
    >
      {/* About */}
      <div
        ref={aboutRef}
        className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-24 transition-all duration-700 ${
          aboutInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="lg:col-span-5">
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900] block mb-4">
            About Relio
          </span>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#1c1b1d]">
            Built by founders,
            <br />
            for founders.
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-base md:text-lg text-[#5a4138] leading-relaxed mb-6">
            We started Relio after watching brilliant founders lose deals — not because their
            product was weak, but because they let relationships go cold. Traditional CRMs are
            built for sales teams, not founders managing the most complex, high-stakes network
            of their careers.
          </p>
          <p className="text-base md:text-lg text-[#5a4138] leading-relaxed mb-6">
            Relio is different. We don't ask you to manually log every interaction. Instead, we
            quietly analyze your email history using AI to build a living map of your relationships.
            We score warmth. We detect decay. We draft follow-ups. We find intro paths.
          </p>
          <p className="text-base md:text-lg text-[#5a4138] leading-relaxed">
            Our mission is simple: make sure no founder ever loses a deal because they forgot to
            follow up.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-10">
            {[
              { value: "12K+", label: "Founders" },
              { value: "$2.8B", label: "Deals Tracked" },
              { value: "98%", label: "Retention" },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl md:text-3xl font-semibold text-[#1c1b1d] tracking-tight">{stat.value}</p>
                <p className="text-xs uppercase tracking-wider text-[#5a4138] mt-1 font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div ref={reviewsRef}>
        <div
          className={`transition-all duration-700 ${
            reviewsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900] block mb-4">
            Customer Reviews
          </span>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#1c1b1d] mb-12">
            Trusted by the best.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {reviews.map((review, i) => (
            <div
              key={review.name}
              data-testid={`review-${i}`}
              className={`
                bg-[#f6f2f5] rounded-3xl p-8 relative group hover:bg-[#f0edf0] transition-all duration-500
                ${reviewsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
              `}
              style={{ transitionDelay: `${i * 120 + 200}ms` }}
            >
              <Quote size={24} className="text-[#e2bfb2] mb-4" />
              <p className="text-sm text-[#1c1b1d] leading-relaxed mb-6">
                "{review.text}"
              </p>

              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: review.rating }).map((_, j) => (
                  <Star key={j} size={12} className="text-[#cc4900] fill-[#cc4900]" />
                ))}
              </div>

              <div className="flex items-center gap-3">
                <img
                  src={review.image}
                  alt={review.name}
                  className="w-10 h-10 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <p className="text-sm font-medium text-[#1c1b1d]">{review.name}</p>
                  <p className="text-xs text-[#5a4138]">{review.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
